import { useParsedIdsFromStrings } from 'modules/queryBuilder/hooks/useParsedIdsFromStrings';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DEFAULT_TYPEAHEAD_QUERY_LIMIT } from '../constants';
import { useSelectedCampaigns, useSuggestedCampaigns } from './useCampaignsQuery';

type UseCampaignOptionsProps = {
  search: string;
  values: string[] | null;
};

export const useCampaignOptions = ({
  search,
  values, // array of selected ids
}: UseCampaignOptionsProps) => {
  const [selectedCampaignPage, setSelectedCampaignPage] = useState<number>(0); // total fetched selected campaign pages
  const [suggestedCampaignPage, setSuggestedCampaignPage] = useState<number>(0); // total fetched suggested campaign pages
  const [numSelectedCampaigns, setNumSelectedCampaigns] = useState<number>(values?.length ?? 0);

  const keepFetchingSelectedCampaigns = useMemo(() => {
    return (
      !!numSelectedCampaigns &&
      numSelectedCampaigns > (selectedCampaignPage + 1) * DEFAULT_TYPEAHEAD_QUERY_LIMIT.limit
    );
  }, [numSelectedCampaigns, selectedCampaignPage]);

  const {
    campaigns: suggestedCampaigns,
    campaignsCount,
    didError: didSuggestedCampaignsError,
    isLoading: isSuggestedCampaignsLoading,
  } = useSuggestedCampaigns(search, suggestedCampaignPage);

  const selectedIds = useParsedIdsFromStrings(values ?? []);
  const {
    campaigns: selectedCampaigns,
    campaignsCount: selectedCampaignsCount,
    didError: didSelectedCampaignsError,
    isLoading: isSelectedCampaignsLoading,
  } = useSelectedCampaigns(selectedIds, search, selectedCampaignPage);

  useEffect(() => {
    if (!isSelectedCampaignsLoading) {
      setNumSelectedCampaigns(selectedCampaignsCount);
    }
  }, [selectedCampaignsCount, isSelectedCampaignsLoading]);

  const options = useMemo(() => {
    // Don't return unless selected campaigns have all been fetched
    if (keepFetchingSelectedCampaigns && !!numSelectedCampaigns) {
      return [];
    }

    return suggestedCampaigns
      .filter((campaign) => !values?.includes(campaign.id.toString()))
      .map((campaign) => ({
        title: campaign.name,
        value: campaign.id.toString(),
        templateId: campaign.templateId,
      }));
  }, [keepFetchingSelectedCampaigns, numSelectedCampaigns, suggestedCampaigns, values]);

  const selectedOptions = useMemo(
    () =>
      selectedCampaigns.map((campaign) => ({
        title: campaign.name,
        value: campaign.id.toString(),
        templateId: campaign.templateId,
      })),
    [selectedCampaigns],
  );

  const didError = didSuggestedCampaignsError || didSelectedCampaignsError;

  const canFetch = useMemo(() => {
    const areCampaignsStillLoading = isSuggestedCampaignsLoading || isSelectedCampaignsLoading;
    const areAllCampaignsFetched =
      campaignsCount <= (suggestedCampaignPage + 1) * DEFAULT_TYPEAHEAD_QUERY_LIMIT.limit;

    return !areCampaignsStillLoading && !areAllCampaignsFetched;
  }, [
    isSuggestedCampaignsLoading,
    isSelectedCampaignsLoading,
    suggestedCampaignPage,
    campaignsCount,
  ]);

  const onFetchMoreCampaigns = useCallback(() => {
    if (canFetch) {
      if (keepFetchingSelectedCampaigns) {
        setSelectedCampaignPage(selectedCampaignPage + 1);
      } else {
        setSuggestedCampaignPage(suggestedCampaignPage + 1);
      }
    }
  }, [keepFetchingSelectedCampaigns, selectedCampaignPage, suggestedCampaignPage, canFetch]);

  const resetCampaignPages = () => {
    setSelectedCampaignPage(0);
    setSuggestedCampaignPage(0);
  };

  return {
    options: options || [],
    selectedOptions: selectedOptions || [],
    didError,
    isSuggestedCampaignsLoading,
    isSelectedCampaignsLoading,
    suggestedCampaignPage,
    selectedCampaignPage,
    resetCampaignPages,
    onFetchMoreCampaigns,
    campaignsCount,
  };
};
